import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowLeft, Share2, Facebook, Twitter, FileText, Download } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const [articleRes, allArticlesRes] = await Promise.all([
          axios.get(`${API}/articles/${id}`),
          axios.get(`${API}/articles?limit=4`),
        ]);
        setArticle(articleRes.data);
        setRelatedArticles(
          allArticlesRes.data.filter((a) => a.id !== id).slice(0, 3)
        );
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "partite":
        return "Partite";
      case "comunicati":
        return "Comunicato Ufficiale";
      case "scuola_calcio":
        return "Scuola Calcio";
      default:
        return "News";
    }
  };

  if (loading) {
    return (
      <div data-testid="article-page-loading">
        <Navbar />
        <div className="pt-24 lg:pt-32 min-h-screen bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <div className="animate-pulse">
              <div className="h-8 bg-slate-200 rounded w-3/4 mb-6" />
              <div className="h-64 bg-slate-200 rounded-xl mb-6" />
              <div className="space-y-3">
                <div className="h-4 bg-slate-200 rounded w-full" />
                <div className="h-4 bg-slate-200 rounded w-full" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div data-testid="article-not-found">
        <Navbar />
        <div className="pt-24 lg:pt-32 min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">
              Articolo non trovato
            </h1>
            <Link to="/news" className="text-[#0047AB] hover:underline">
              Torna alle news
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      <Navbar />

      {/* Hero Image */}
      <section className="pt-20 lg:pt-24">
        <div className="h-64 lg:h-96 relative overflow-hidden">
          <img
            src={
              article.image_url ||
              "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920"
            }
            alt={article.title}
            className="w-full h-full object-cover"
            data-testid="article-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      </section>

      {/* Article Content */}
      <section className="relative -mt-20 pb-16" data-testid="article-content">
        <div className="max-w-4xl mx-auto px-4">
          <article className="bg-white rounded-2xl shadow-xl p-6 lg:p-12">
            {/* Back link */}
            <Link
              to="/news"
              data-testid="back-to-news"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-[#0047AB] mb-6 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna alle news
            </Link>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-xs font-semibold text-[#0047AB] bg-blue-50 px-3 py-1 rounded-full">
                {getCategoryLabel(article.category)}
              </span>
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(article.published_date || article.created_at)}
              </span>
              {article.author && (
                <span className="text-sm text-slate-500">
                  di <span className="font-medium text-slate-700">{article.author}</span>
                </span>
              )}
            </div>

            {/* Title */}
            <h1
              className="text-2xl lg:text-4xl font-bold text-slate-900 mb-6"
              data-testid="article-title"
            >
              {article.title}
            </h1>

            {/* Excerpt */}
            <p className="text-lg text-slate-600 mb-8 font-medium leading-relaxed border-l-4 border-[#0047AB] pl-4">
              {article.excerpt}
            </p>

            {/* Content */}
            <div
              className="prose prose-lg max-w-none text-slate-700"
              data-testid="article-body"
            >
              {article.content.split("\n").map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* PDF Allegato */}
            {article.pdf_url && (
              <div className="mt-8 p-4 sm:p-6 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 text-sm sm:text-base truncate">{article.pdf_name || "Documento allegato"}</p>
                      <p className="text-xs sm:text-sm text-slate-500">File PDF</p>
                    </div>
                  </div>
                  <a
                    href={article.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0047AB] text-white rounded-lg hover:bg-[#003380] transition-colors text-sm sm:text-base w-full sm:w-auto flex-shrink-0"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    Scarica PDF
                  </a>
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-12 pt-8 border-t">
              <div className="flex items-center gap-4">
                <span className="text-slate-500 text-sm flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Condividi
                </span>
                <button
                  data-testid="share-facebook"
                  className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </button>
                <button
                  data-testid="share-twitter"
                  className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Articoli Correlati
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/news/${related.id}`}
                    data-testid={`related-article-${related.id}`}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={
                          related.image_url ||
                          "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400"
                        }
                        alt={related.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-slate-900 line-clamp-2 hover:text-[#0047AB] transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticlePage;
